import { FCX } from "@datassential/platform-ui-lib";
import { ComponentProps, ReactNode, useEffect, useId, useMemo, useRef } from "react";
import Field from "@components/Field";
import Dropdown from "@components/Dropdown";
import cn from "classnames";
import { DropdownProps } from "@components/Dropdown/Dropdown";
import { FieldProps } from "@components/Field/Field";

import { IoIosArrowDown } from "react-icons/io";
import useOutsideClick from "@hooks/useOutsideClick";

type Modifiers = 'highlighted'

const SelectComponent: FCX<{
    isFluidWidth?: boolean;
    label?: FieldProps["label"];
    fieldProps?: Omit<FieldProps, 'label'>;
    dropdownProps?: Omit<DropdownProps, 'tooltipId' | 'id'>;
    thumbnailText?: ReactNode;
    isOpened: boolean;
    setIsOpened: (isOpened: boolean) => void;
    onOpenChange?: (isOpened: boolean) => void;
    modifiers?: Modifiers[];
}> = ({
    label,
    isFluidWidth,
    fieldProps,
    dropdownProps,
    thumbnailText,
    isOpened,
    setIsOpened,
    className,
    testId,
    style,
    children,
    onOpenChange,
    modifiers = [],
}) => {
    const id = useId();
    const ref = useRef(null);

    useEffect(() => {
        if (onOpenChange) {
            onOpenChange(isOpened);
        }
    }, [isOpened, onOpenChange]);

    const fieldModifiers = useMemo(
        () => {
            const result: FieldProps['modifiers'] = [...(fieldProps?.modifiers ?? ['min-width'])];

            if (isOpened) {
                result.push('active');
            }

            return result;
        },
        [fieldProps?.modifiers, isOpened]
    );

    useOutsideClick({
        ref,
        callback: () => setIsOpened(false),
        isDisabled: !isOpened,
        ignoreNodesIds: [id],
    });

    return (
        <div
            className={cn(
                "SelectComponent",
                className,
                isOpened && 'is-opened',
                ...modifiers.map((m) => `is-${m}`)
            )}
            data-testid={testId}
            style={style}
            ref={ref}
        >
            <Field
                label={label}
                className={cn(
                    "SelectComponent__field",
                    fieldProps?.className,
                )}
                style={{
                    ...fieldProps?.style,
                    width: isFluidWidth ? 'auto' : undefined,
                }}
                modifiers={fieldModifiers}
            >
                <div
                    className="SelectComponent__thumbnail"
                    onClick={() => setIsOpened(!isOpened)}
                    data-tooltip-id={id}
                    data-testid="selectThumbnailInput"
                >
                    <div className="SelectComponent__thumbnail-inner">
                        {thumbnailText}
                    </div>
                    <IoIosArrowDown
                        className="SelectComponent__thumbnail-icon"
                    />
                </div>
            </Field>
            <Dropdown
                {...dropdownProps}
                isOpened={isOpened}
                id={id}
                tooltipId={id}
                testId={`blockSelectDropdown${id}`}
            >
                {children}
            </Dropdown>
        </div>
    );
};

export type SelectComponentProps = ComponentProps<typeof SelectComponent>;

export default SelectComponent;